import { Button, Input } from '@design-systems/canvas-ui'
import Icon, { CloseIcon } from '@design-systems/react-icons'
import { Table } from 'components/table-compat/table-compat'
import { Container } from 'components/container/Container'
import { ErrorAlert } from 'components/error-fallback'
import { BasicPlaceholder } from 'components/placeholder'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { useRecall } from 'hooks/operations/recall/useRecall'
import useCountdown from 'hooks/utils/useCountdown'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useMemo, useState } from 'react'
import { buildDataTableObject, TableData } from 'utils/data'
import { isCompany, isDistrict, isRegion, isStore } from 'utils/location'
import { useLogClick } from 'utils/analytics'
import { Constants } from '../constants'
import './recall.scss'

export type RecallItem = {
    store_id: number
    t_store_id: string
    ir_number: string
    region_id?: string
    group_id?: string
    district_id?: string
}

export const Recall = () => {
    const { t, currentLocation, env, logout, session, isEnglish } =
        useEssentials()
    const { operationalMetrics } = TRANSLATION_KEYS
    const { recall } = operationalMetrics
    const { headings, search: searchKeys } = recall
    const { data, isLoading, isError, error, refetch } = useRecall({
        env,
        accessToken: session?.accessToken ?? '',
        filters: isCompany(currentLocation)
            ? {}
            : { location_id: currentLocation },
    })
    const count = useCountdown(5, logout, error as Error)
    const { logClick } = useLogClick('recall')

    const [searchTerm, setSearchTerm] = useState<string>('')

    const filteredItems = useMemo(() => {
        if (!data) return null
        const items = data as RecallItem[]
        const trimmed = searchTerm.trim()
        if (!trimmed) return items
        const lower = trimmed.toLowerCase()
        return items.filter((item) =>
            item.ir_number.toLowerCase().includes(lower),
        )
    }, [data, searchTerm])

    const tableData: TableData | null = useMemo(() => {
        if (!filteredItems) return null

        // Store level: show ir_number only
        if (isStore(currentLocation)) {
            return {
                headings: [{ value: t(headings.irNumber) }],
                rows: filteredItems.map((item) => [{ value: item.ir_number }]),
            }
        }

        // District level: store_id is drillable, no hierarchy column
        if (isDistrict(currentLocation)) {
            return {
                headings: [
                    { value: t(headings.store) },
                    { value: t(headings.irNumber) },
                ],
                rows: filteredItems.map((item) => [
                    {
                        value: item.t_store_id,
                        url: `/operations/recall?locationId=${item.t_store_id}`,
                    },
                    { value: item.ir_number },
                ]),
            }
        }

        // Target, Region, Group levels: hierarchy column is first
        const isTargetLevel = isCompany(currentLocation)
        const isRegionLevel = isRegion(currentLocation)

        const hierarchyHeading = isTargetLevel
            ? t(headings.region)
            : isRegionLevel
              ? t(headings.group)
              : t(headings.district)

        return {
            headings: [
                { value: hierarchyHeading },
                { value: t(headings.store) },
                { value: t(headings.irNumber) },
            ],
            rows: filteredItems.map((item) => {
                const hierarchyValue = isTargetLevel
                    ? (item.region_id ?? '')
                    : isRegionLevel
                      ? (item.group_id ?? '')
                      : (item.district_id ?? '')

                const hierarchyCell = hierarchyValue
                    ? {
                          value: hierarchyValue,
                          url: `/operations/recall?locationId=${hierarchyValue}`,
                      }
                    : { value: hierarchyValue }

                return [
                    hierarchyCell,
                    { value: item.t_store_id },
                    { value: item.ir_number },
                ]
            }),
        }
    }, [filteredItems, t, headings, currentLocation])

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={3} />
            </div>
        )
    }

    if (isError || !data) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    const now = new Date()
    const dateLocale = isEnglish ? undefined : es
    const formattedDate = `${t(TRANSLATION_KEYS.timePeriods.today)}, ${format(now, Constants.DATE_FORMAT, { locale: dateLocale })}`

    const isStoreLvl = isStore(currentLocation)
    const isDistrictLvl = isDistrict(currentLocation)
    const columnWidths = isStoreLvl
        ? [100]
        : isDistrictLvl
          ? [80, 100]
          : isEnglish
            ? [70, 65, 80]
            : [80, 65, 80]

    const hasNoResults = filteredItems !== null && filteredItems.length === 0

    return (
        <Container className="hc8-pa-3x">
            <p className="hc8-mb-3x">{formattedDate}</p>
            <div className="recall-search hc8-mb-3x">
                <Input.Label htmlFor="recall-search-input">
                    {t(searchKeys.label)}
                </Input.Label>
                <div className="recall-search__input-row">
                    <Input.Text
                        id="recall-search-input"
                        placeholder={t(searchKeys.placeholder)}
                        value={searchTerm}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => setSearchTerm(event.target.value)}
                        aria-controls="recall-results"
                    />
                    {searchTerm && (
                        <Button
                            isIconOnly
                            aria-label={t(searchKeys.clearAriaLabel)}
                            onClick={() => {
                                logClick('clear recall search')
                                setSearchTerm('')
                            }}
                            variant="ghost"
                            className="recall-search__clear-btn"
                        >
                            <Icon svg={CloseIcon} aria-hidden />
                        </Button>
                    )}
                </div>
            </div>
            <div
                id="recall-results"
                role="region"
                aria-label={t(searchKeys.label)}
                aria-live="polite"
                aria-atomic="true"
            >
                {hasNoResults ? (
                    <p className="recall-search__no-results">
                        {t(searchKeys.noResults)}
                    </p>
                ) : (
                    tableData && (
                        <Table
                            data={buildDataTableObject({
                                currentData: tableData,
                                widths: columnWidths,
                                sortable: true,
                            })}
                            scrollableWidth={true}
                            alternateRowColor={true}
                        />
                    )
                )}
            </div>
        </Container>
    )
}
